<script setup lang="ts">
/**
 * 首页。
 *
 * 原实现是服务端组件（无状态、无请求），移植过来就是一个纯静态页面——
 * 唯一的"动态"是领域列表来自契约的 `DOMAINS`，顺序由契约决定，不要本地排序。
 */

import { CRISIS_HOTLINE, DOMAINS, DOMAIN_META } from '@spirit/contracts';

import AppFooter from '../components/layout/AppFooter.vue';
import Button from '../components/ui/Button.vue';

const STEPS: ReadonlyArray<{ title: string; body: string }> = [
  {
    title: '1. 说清情况',
    body: '写下孩子的年龄、最近发生的一件事、你希望改变的地方。具体的事实比概括的评价更有用。',
  },
  {
    title: '2. 拿到可执行建议',
    body: '回答会给出可以直接尝试的做法，并说明为什么这样做，而不是笼统地讲道理。',
  },
  {
    title: '3. 按需追问',
    body: '同一次对话里可以继续补充细节，回答会结合前面的内容，不用重复介绍背景。',
  },
];
</script>

<template>
  <div class="mx-auto w-full max-w-5xl px-4 py-10">
    <section class="text-center">
      <p class="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700">
        文化服务平台 · 家庭教育为核心
      </p>
      <h1 class="mt-4 text-2xl font-semibold leading-snug text-slate-900 sm:text-3xl">
        把亲子沟通里的难题，<br class="hidden sm:block" />说清楚，再找到下一步怎么做
      </h1>
      <p class="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
        智家教育面向家庭教育的日常困扰：学习习惯、情绪管理、亲子沟通、青春期变化，
        给出可执行的参考建议；同时提供心理、健康、法律与百科领域的知识性咨询。
      </p>
      <div class="mt-6 flex flex-wrap justify-center gap-3">
        <RouterLink to="/chat"><Button size="lg">开始咨询</Button></RouterLink>
        <RouterLink to="/login"><Button size="lg" variant="secondary">登录 / 注册</Button></RouterLink>
      </div>
      <p class="mt-3 text-xs text-slate-500">未登录也可以先浏览；开始对话需要登录以保存会话与额度。</p>
    </section>

    <section class="mt-12" aria-labelledby="domains-heading">
      <h2 id="domains-heading" class="text-lg font-semibold text-slate-900">五个咨询领域</h2>
      <p class="mt-1 text-sm text-slate-600">
        选择一个领域进入对话，服务端会按提问内容自动判断；也可以直接提问，不必先选。
      </p>
      <ul class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <li v-for="domain in DOMAINS" :key="domain">
          <RouterLink
            :to="`/chat?domain=${domain}`"
            class="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
          >
            <span class="flex items-center gap-2">
              <span class="text-sm font-medium text-slate-900">{{ DOMAIN_META[domain].label }}</span>
              <span
                v-if="DOMAIN_META[domain].safetyLevel === 'high' || DOMAIN_META[domain].safetyLevel === 'critical'"
                class="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800"
              >
                谨慎使用
              </span>
            </span>
            <span class="mt-1.5 text-xs leading-relaxed text-slate-600">{{ DOMAIN_META[domain].safetyNote }}</span>
            <span v-if="DOMAIN_META[domain].requiresAgeBand" class="mt-2 text-[11px] text-slate-400">
              建议说明孩子年龄段
            </span>
          </RouterLink>
        </li>
      </ul>
    </section>

    <section class="mt-12" aria-labelledby="steps-heading">
      <h2 id="steps-heading" class="text-lg font-semibold text-slate-900">怎么用</h2>
      <ol class="mt-4 grid gap-3 sm:grid-cols-3">
        <li v-for="step in STEPS" :key="step.title" class="rounded-xl border border-slate-200 bg-white p-4">
          <p class="text-sm font-medium text-slate-900">{{ step.title }}</p>
          <p class="mt-1.5 text-xs leading-relaxed text-slate-600">{{ step.body }}</p>
        </li>
      </ol>
    </section>

    <section class="mt-12 rounded-xl border border-red-200 bg-red-50 p-4" aria-labelledby="crisis-heading">
      <h2 id="crisis-heading" class="text-sm font-medium text-red-900">如果你现在处于危机中</h2>
      <p class="mt-1.5 text-sm leading-relaxed text-red-900">
        本平台不能提供紧急援助。请立即拨打<a
          :href="`tel:${CRISIS_HOTLINE}`"
          class="mx-1 font-medium underline underline-offset-2"
          >全国心理援助热线 {{ CRISIS_HOTLINE }}</a
        >（24 小时），或拨打 110 / 120。
      </p>
    </section>
  </div>

  <AppFooter class="mt-auto" />
</template>
